import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useIntl } from "react-intl";

import useOwnObjects from "../../hooks/useOwnObjects";

import Alert from "../../components/alert/Alert";
import SettingIcon from "../../components/icons/Setting";
import Th from "../../components/ui/table/Th";
import Tbody from "../../components/ui/table/Tbody";
import Td from "../../components/ui/table/Td";
import Tr from "../../components/ui/table/Tr";

import { getFeatureLabel } from "../../utils/features";
import { FeatureEnum } from "../../types/feature";
import { getCompleteUrl } from "../../utils/url";
import { Helmet } from "react-helmet-async";
import PageSectionHeader from "../../components/PageSectionHeader";

export default function OwnObjects() {
  const intl = useIntl();
  const {error, isLoading, fetchOwnObjects, handleSort, sortedObject, currentSortTarget} = useOwnObjects();

  useEffect(() => fetchOwnObjects(), [fetchOwnObjects]);

  return (
    <>
      <Helmet>
        <title>
          {intl.formatMessage(
            { id: "equipment.own.documentTitle" },
            { appName: import.meta.env.VITE_APP_NAME }
          )}
        </title>
      </Helmet>
      <PageSectionHeader className="mb-10">
        <h1 className="py-2 flex items-center gap-4 rounded-md">
          <SettingIcon className="w-5 h-5"/>
          <span className="text-shadow text-xl">
            {intl.formatMessage({ id: "equipment.own.heading" })}
          </span>
        </h1>
      </PageSectionHeader>
      
      { error ?
        <Alert className="my-5 w-fit" type="error" message={error.message} /> : ''
      }

      {
        sortedObject && sortedObject.length > 0 ? (
          <div className="py-2 px-1 overflow-x-auto">
            <table className="w-full max-w-screen-lg">
              <thead>
                <tr className="capitalize">
                  <Th width="3%">{intl.formatMessage({ id: "equipment.own.colNumber" })}</Th>
                  <Th width="150px">{intl.formatMessage({ id: "equipment.own.colName" })}</Th>
                  <Th sorted={true} sort={{active: currentSortTarget === 'url', target: 'url', onSort: handleSort}}>
                    {intl.formatMessage({ id: "equipment.own.colBadgeNumber" })}
                  </Th>
                  <Th sorted={true} sort={{active: currentSortTarget === 'feat', target: 'feat', onSort: handleSort}}>
                    {intl.formatMessage({ id: "equipment.own.colFeature" })}
                  </Th>
                  <Th width="75px">{intl.formatMessage({ id: "equipment.own.colScan" })}</Th>
                  <Th width="50px">{''}</Th>
                </tr>
                <tr><th className="pb-[14px]" colSpan={4}></th></tr>
              </thead>
              <Tbody>
                { sortedObject.map((obj, i) => (
                    <Tr key={obj.id} isFirstRow={i === 0}>
                      <Td>{i+1}</Td>
                      <Td>{ obj.name || ''}</Td>
                      <Td>
                        <a className={`${obj.feature.type ? 'text-primary' : 'pointer-events-none'} hover:underline`}
                        href={getCompleteUrl(obj.url)} target="_target">{obj.url}</a>
                      </Td>
                      <Td>
                        {obj.feature.type
                          ? getFeatureLabel(intl, obj.feature.type)!
                          : intl.formatMessage({ id: "common.notConfigured" })}
                      </Td>
                      <Td>{obj.trackingCount}</Td>
                      <Td isLastCell={true}>
                        <Link to={{pathname: `./../feature/edit/${obj.feature.type ?? FeatureEnum.Human}`, search: `id=${obj.id}&objCode=${obj.url}` }}
                          className="block w-fit text-shadow hover:text-black">
                          <SettingIcon className="w-5 h-5"/>
                        </Link>
                      </Td>
                    </Tr>
                  ))
                }
              </Tbody>
            </table>
          </div>     
        ) : !isLoading  && (
          <p className="text-4xl font-semibold fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            {intl.formatMessage({ id: "equipment.own.empty" })}
          </p>
        )
      }

      {
        isLoading && (
          <p className="my-5 text-center text-xs">
            {intl.formatMessage({ id: "common.loading" })}
          </p>
        )
      }
    </>
  )
}
